'use client'

import { useEffect, useState, useSyncExternalStore } from "react"
import MainCanvas from "../components/three/mainCanvas";
import { Vignette } from "../components/ui/Vignette";
import { NPCStatsHUD } from "../components/ui/NPCStatsHUD";
import { ChatFeed } from "../components/ui/ChatFeed"
import { JournalHUD } from "../components/ui/JournalHUD"
import { OmenHUD } from "../components/ui/OmenHUD";
import { WorldClock } from "../components/ui/WorldClock";
import { DetectionOverlay } from "../components/ui/DetectionOverlay";
import { ControlsHint } from "../components/ui/ControlsHint";
import { StatusLine } from "../components/ui/StatusLine";
import { BirdReply } from "../components/ui/BirdReply";
import { PhotoMode } from "../components/ui/PhotoMode";
import { HudMenu } from "../components/ui/HudMenu";
import { ActionsHud } from "../components/ui/ActionsHud";
import { VendingHud } from "../components/ui/VendingHud";
import { BagPanel } from "../components/ui/BagPanel";
import { JobCentreHud } from "../components/jobs/JobCentreHud";
import { MiniMap } from "../components/ui/MiniMap";
import { TouchControls } from "../components/ui/TouchControls";
import { FirstPersonHud } from "../components/ui/FirstPersonHud";
import { SignalBar } from "../components/ui/SignalBar";
import { BrowsePopup } from "../components/ui/BrowsePopup";
import { DiskTerminal } from "../components/ui/DiskTerminal";
import { AboutDrawer } from "../components/ui/AboutDrawer";
import { VictorIntro } from "../components/ui/VictorIntro";
import { OfferingsDock } from "../components/ui/OfferingsDock";
import { SinceStrip } from "../components/ui/SinceStrip";
import NoticeWatcher from "../components/ui/NoticeWatcher";
import { ProfileDrawer } from "../components/ui/ProfileDrawer";
import { CalendarDrawer } from "../components/ui/CalendarDrawer";
import { StreamOverlay } from "../components/ui/StreamOverlay";
import { PostersDrawer } from "../components/ui/PostersDrawer";
import Toasts from "../components/ui/Toasts";
import CrashGuard from "../components/ui/CrashGuard";
import { LoadingVeil } from "../components/ui/LoadingVeil";
import { UIOverlayController } from "../components/ui/UIOverlayController";
import { LifeNow } from "../components/ui/LifeNow";
import { DroneOverlay } from "../components/three/DroneCamera";
import useGameStore from "../stores/gameStore";
import { FEATURES, HAS_AUTH } from "../lib/features";

export default function Home() {
  // Informational HUDs gate on this. Cinematic frame + vignette stay always.
  // Defaults: visible on every device, Tab toggles off (UIOverlayController).
  const uiVisible = useGameStore((s) => s.uiVisible)
  // Drone mode hides ALL site chrome for full immersion (HUDs, brain,
  // council, onboarding modal stay closed). Vignette + viewfinder stay
  // because they're cinematic dressing, not info.
  const droneActive = useGameStore((s) => s.droneActive)
  // Photo mode strips the screen for the shot; PhotoMode draws its own
  // controls, so it sits outside the chrome gate below.
  const photoMode = useGameStore((s) => s.photoMode)
  const shrineUnseen = useGameStore((s) => s.shrineUnseen)
  const journalUnseen = useGameStore((s) => s.journalUnseen)

  // First-visit cinematic. introSeen is persisted by zustand middleware
  // (localStorage), so a returning visitor lands straight on the world.
  // Anyone curious later can reopen the info modal via the brain icon.
  const introSeen = useGameStore((s) => s.introSeen)

  // Defer the intro render until after hydration. Zustand persist
  // re-reads localStorage on the client only — rendering it during SSR
  // would always show the intro for one paint before snapping away,
  // which looks broken. `hydrated` flips true once the client mounts.
  const hydrated = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  )

  // The about drawer, separate from the cinematic. Always-available
  // reference card. State stays local because no other component cares.
  const [aboutOpen, setAboutOpen] = useState(false)
  const closeAbout = () => setAboutOpen(false)

  // Intro is feature-flagged so prod stays clean until launch. Set
  // NEXT_PUBLIC_FEATURE_INTRO=true locally (.env.local) to see it.
  const showIntro = FEATURES.intro && hydrated && !introSeen && !droneActive
  // Hide HUDs / chrome while the intro is up — same rationale as drone
  // mode. Vignette + viewfinder stay (cinematic dressing, not info).
  // The Pino cam (?cam=pino) is for a stream: no buttons, no panels,
  // just the stream overlay over his view.
  const pinoCam = useGameStore((s) => s.pinoCam)
  const chromeHidden = droneActive || showIntro || photoMode || pinoCam
  // His bag shuts when one of those comes up, and a tap on him while one is up does not leave it waiting behind it
  // (the review of 3 Nov: tapped during the intro, it opened the moment the intro ended)
  const bagOpen = useGameStore((s) => s.bagOpen)
  const setBagOpen = useGameStore((s) => s.setBagOpen)
  useEffect(() => { if (chromeHidden && bagOpen) setBagOpen(false) }, [chromeHidden, bagOpen, setBagOpen])

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 font-sans dark:bg-black">
      {/* Its own net: a loop inside the canvas names itself "(world)" in
          the console and takes only the world down, not the drawers. */}
      {/* selfHeal: the update-depth loop can only fire while the renderer
          promise is pending; by the time the boundary catches it the
          promise is cached and resolved, so a remount takes the
          synchronous path and comes back clean. Better a sub-second blip
          than a black world with the HUD floating on it. */}
      <CrashGuard tag="world" selfHeal><MainCanvas /></CrashGuard>

      {pinoCam && <StreamOverlay />}

      {/* Resolves initial visibility + binds Tab toggle. */}
      <UIOverlayController />

      {/* Drone-mode key handler (F entry / Esc exit) + on-screen overlay. */}
      <DroneOverlay />

      {/* Photo mode owns the whole screen when open, and binds P either way. */}
      <PhotoMode />

      {/* First-visit cinematic. Marks introSeen on dismiss; never shows
          again unless localStorage is cleared. Brain-icon info modal
          continues to be reachable for returning visitors. */}
      {/* Every component in this cluster calls usePrivy, so without an app
          id none of them may mount: there is no provider to answer them. */}
      {showIntro && HAS_AUTH && <VictorIntro onClose={() => { /* zustand handles state */ }} />}

      {/* The auth island: privy loads lazily in a parallel subtree and
          talks to the page through the store, so its megabytes never race
          the world for the boot main thread. */}

      {/* Every panel's "that worked / that didn't", one rack, bottom right.
          Outside the gates so a toast pushed as a drawer closes still gets
          its moment. */}
      <Toasts />

      {/* Polls what has moved since you last looked. Not gated on the HUD
          being visible: hiding the chrome should not stop the world knowing
          something happened, or the dot would be missing the moment you
          brought the menu back. */}
      <NoticeWatcher />

      {/* The corner menu answers Tab like everything else now. It is chrome,
          and "hide the UI" that leaves five buttons on screen is not hiding
          the UI. Tab brings it straight back. */}
      {uiVisible && !chromeHidden && (
        <HudMenu
          onAbout={() => setAboutOpen(true)}
          journalUnseen={journalUnseen}
          shrineUnseen={shrineUnseen}
        />
      )}

      {/* The town offering you things to do, when you are stood somewhere
          that has some. First-person only by construction: it reads the
          walker's position and there is none in camera mode. */}
      {uiVisible && !chromeHidden && <ActionsHud />}

      {/* His vending machine: the close view, and what a visitor can buy out
          of it, with the drop played when the till sends them back. */}
      {uiVisible && <VendingHud />}
      {/* the job centre, in what was the tech shop: walk up and go in (sim-server world/JobCentre.js) */}
      {uiVisible && <JobCentreHud />}
      {/* His bag: a click on him opens it (components/three/npc/BirdHit.jsx).
          Outside the Tab gate, like About: it is something you opened on
          purpose. Nothing at all while it is shut. */}
      {!chromeHidden && <BagPanel />}

      {/* Soft warm edge falloff. The camcorder ViewfinderFrame that used to
          sit here went with the camera rig: corner brackets framed the world
          as footage being recorded, which is the opposite of being somewhere.
          The component is kept (components/ui/ViewfinderFrame.jsx) in case a
          deliberately-cinematic mode ever wants it back. */}
      <Vignette intensity={0.32} color="26, 16, 10" />

      {/* First-person interaction layer: crosshair + aim label, wall-bump
          impact flash, and the offering viewer (crystal click). Gates itself
          on view mode internally. */}
      {!chromeHidden && <FirstPersonHud />}
      {FEATURES.signals && !chromeHidden && <SignalBar />}
      {/* His answer to a walker's mark, said in words. */}
      {FEATURES.signals && !chromeHidden && <BirdReply />}
      {/* Thumb stick + look-drag for phones. Outside the uiVisible gate on
          purpose: Tab hiding the HUD must never leave a phone unable to walk. */}
      {!chromeHidden && <TouchControls />}

      {/* Toggleable informational overlay (Tab on desktop, always on mobile)
          AND not flying AND not in intro. About used to unmount this lot
          while it was open, which made sense when it was a modal floating in
          the middle of the screen. It is a drawer now and dims what is behind
          it like every other one, so the HUD stays where you left it. */}
      {uiVisible && !chromeHidden && (
        <>
          <NPCStatsHUD />
          <LifeNow />
          {/* What happened to you while you were away, once per visit,
              signed in. Sits under the needs bar; dismisses itself. */}
          {HAS_AUTH && <SinceStrip />}
          <ChatFeed />
          <OmenHUD />
          <JournalHUD npc="victor" />
          <WorldClock />
          {FEATURES.detection && <DetectionOverlay />}
          <StatusLine />
          <MiniMap />
          <ControlsHint />
          <BrowsePopup />
          {/* a floppy disk from his vending machine in the drive at the cafe: what is on it and what it did */}
          <DiskTerminal />
          {/* The shrine as an always-on presence: offer without leaving
              the world. /shrine remains the deep view. */}
          {/* Self-healing: extensions (password managers, wallets) inject
              nodes into text-heavy form UI and React's next reorder throws
              insertBefore errors — a quiet remount beats the crash card. */}
          {FEATURES.shrine && HAS_AUTH && (
            <CrashGuard tag="offerings" selfHeal><OfferingsDock /></CrashGuard>
          )}
          {HAS_AUTH && <ProfileDrawer />}
          <CalendarDrawer />
          {HAS_AUTH && <PostersDrawer />}
        </>
      )}

      {/* About stays outside the Tab gate: it is something you opened on
          purpose, and having it vanish under your hands would be a different
          kind of surprise from the HUD going quiet.

          It sits AFTER the HUD rather than before it. The chat feed and the
          status line are z-50, the same as every drawer, so the tie is
          settled on DOM order: mounted first, the drawer had his diary
          printed across the middle of it. The other drawers never showed
          this because they live inside the block above. */}
      {!chromeHidden && (
        <AboutDrawer open={aboutOpen} onClose={closeAbout} />
      )}

      {/* Over everything until the models are in. Without it the first thing
          a visitor sees is a black screen with the HUD floating on it, which
          reads as broken rather than loading. Rendered last as well as at the
          top z-index: the compass shares a stacking context and won on DOM
          order when the veil sat first. */}
      <LoadingVeil />

    </div>
  );
}
